import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BookOpen, FileText, Users, BarChart3, FilePlus, Eye, Layers, LogOut, Home, Bell, CheckSquare, Inbox, Send, PieChart, Settings, Menu, X } from 'lucide-react';

export default function DashboardSidebar() {
  const { user, logout, isAdmin, isEditor, isReviewer } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const linkClass = (path) =>
    `flex items-center gap-3 px-3 py-2 text-sm font-sans rounded-md transition-colors ${
      isActive(path)
        ? 'bg-slate-dark text-white font-medium'
        : 'text-gray-600 hover:text-slate-dark hover:bg-gray-50'
    }`;

  const navContent = (
    <>
      {/* User info */}
      <div className="p-4 border-b border-gray-100">
        <p className="text-sm font-sans font-semibold text-slate-dark truncate">{user?.name} {user?.apellidos}</p>
        <p className="text-xs font-sans text-gray-400 truncate">{user?.email}</p>
        <div className="flex gap-1 mt-1.5 flex-wrap">
          {user?.roles?.map(r => (
            <span key={r} className="text-[10px] font-sans px-1.5 py-0.5 rounded-sm bg-trans-blue/10 text-trans-blue font-medium">{r}</span>
          ))}
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {(isAdmin() || isEditor()) && (
          <div className="mb-4">
            <p className="px-3 py-1 text-[10px] font-sans font-semibold tracking-widest uppercase text-gray-400">Editorial</p>
            <div className="flex flex-col gap-0.5 mt-1">
              <Link to="/panel" className={linkClass('/panel')} onClick={() => setOpen(false)} data-testid="sidebar-resumen">
                <BarChart3 className="w-4 h-4" strokeWidth={1.5} />Resumen
              </Link>
              <Link to="/panel/envios" className={linkClass('/panel/envios')} onClick={() => setOpen(false)} data-testid="sidebar-envios">
                <Inbox className="w-4 h-4" strokeWidth={1.5} />Envios Recibidos
              </Link>
              <Link to="/panel/revision" className={linkClass('/panel/revision')} onClick={() => setOpen(false)} data-testid="sidebar-revision">
                <Eye className="w-4 h-4" strokeWidth={1.5} />En Revision
              </Link>
              <Link to="/panel/dictaminados" className={linkClass('/panel/dictaminados')} onClick={() => setOpen(false)} data-testid="sidebar-dictaminados">
                <CheckSquare className="w-4 h-4" strokeWidth={1.5} />Dictaminados
              </Link>
              <Link to="/panel/publicados" className={linkClass('/panel/publicados')} onClick={() => setOpen(false)} data-testid="sidebar-publicados">
                <Layers className="w-4 h-4" strokeWidth={1.5} />Publicados
              </Link>
            </div>
          </div>
        )}

        {(isReviewer() && !isAdmin() && !isEditor()) && (
          <div className="mb-4">
            <p className="px-3 py-1 text-[10px] font-sans font-semibold tracking-widest uppercase text-gray-400">Revision</p>
            <div className="flex flex-col gap-0.5 mt-1">
              <Link to="/panel/mis-revisiones" className={linkClass('/panel/mis-revisiones')} onClick={() => setOpen(false)} data-testid="sidebar-mis-revisiones">
                <Eye className="w-4 h-4" strokeWidth={1.5} />Mis Asignaciones
              </Link>
            </div>
          </div>
        )}

        <div className="mb-4">
          <p className="px-3 py-1 text-[10px] font-sans font-semibold tracking-widest uppercase text-gray-400">Autoria</p>
          <div className="flex flex-col gap-0.5 mt-1">
            <Link to="/panel/mis-manuscritos" className={linkClass('/panel/mis-manuscritos')} onClick={() => setOpen(false)} data-testid="sidebar-mis-manuscritos">
              <FileText className="w-4 h-4" strokeWidth={1.5} />Mis Manuscritos
            </Link>
            <Link to="/panel/enviar" className={linkClass('/panel/enviar')} onClick={() => setOpen(false)} data-testid="sidebar-enviar">
              <FilePlus className="w-4 h-4" strokeWidth={1.5} />Enviar Manuscrito
            </Link>
          </div>
        </div>

        {isAdmin() && (
          <div className="mb-4">
            <p className="px-3 py-1 text-[10px] font-sans font-semibold tracking-widest uppercase text-gray-400">Administracion</p>
            <div className="flex flex-col gap-0.5 mt-1">
              <Link to="/panel/usuarios" className={linkClass('/panel/usuarios')} onClick={() => setOpen(false)} data-testid="sidebar-usuarios">
                <Users className="w-4 h-4" strokeWidth={1.5} />Usuaries
              </Link>
              <Link to="/panel/paginas" className={linkClass('/panel/paginas')} onClick={() => setOpen(false)} data-testid="sidebar-paginas">
                <Settings className="w-4 h-4" strokeWidth={1.5} />Paginas CMS
              </Link>
              <Link to="/panel/analiticas" className={linkClass('/panel/analiticas')} onClick={() => setOpen(false)} data-testid="sidebar-analiticas">
                <PieChart className="w-4 h-4" strokeWidth={1.5} />Analiticas
              </Link>
            </div>
          </div>
        )}

        <div className="mb-4">
          <Link to="/panel/notificaciones" className={linkClass('/panel/notificaciones')} onClick={() => setOpen(false)} data-testid="sidebar-notificaciones">
            <Bell className="w-4 h-4" strokeWidth={1.5} />Notificaciones
          </Link>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-100">
        <Link to="/" className="flex items-center gap-3 px-3 py-2 text-sm font-sans text-gray-500 hover:text-slate-dark rounded-md transition-colors" onClick={() => setOpen(false)}>
          <Home className="w-4 h-4" strokeWidth={1.5} /> Ir al sitio
        </Link>
        <button onClick={() => { logout(); setOpen(false); }} className="flex items-center gap-3 px-3 py-2 text-sm font-sans text-red-400 hover:text-red-600 rounded-md transition-colors w-full text-left" data-testid="sidebar-logout">
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> Cerrar Sesion
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-gray-100 flex items-center px-4 gap-3" data-testid="mobile-dashboard-bar">
        <button onClick={() => setOpen(!open)} className="p-1.5 rounded-md hover:bg-gray-100" data-testid="sidebar-mobile-toggle">
          {open ? <X className="w-5 h-5 text-slate-dark" /> : <Menu className="w-5 h-5 text-slate-dark" />}
        </button>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-sm bg-slate-dark flex items-center justify-center">
            <BookOpen className="w-3 h-3 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-serif font-bold text-slate-dark text-sm">Archivos Trans</span>
        </Link>
      </div>

      {/* Mobile overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setOpen(false)} />
      )}

      {/* Sidebar - desktop always visible, mobile as slide-over */}
      <aside
        className={`fixed lg:static z-40 top-0 left-0 h-full w-64 bg-white border-r border-gray-100 flex flex-col shrink-0 transition-transform duration-200 ${
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="dashboard-sidebar"
      >
        {/* Logo - desktop only */}
        <div className="p-5 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
            <div className="w-7 h-7 rounded-sm bg-slate-dark flex items-center justify-center">
              <BookOpen className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-serif font-bold text-slate-dark text-sm">Archivos Trans</span>
          </Link>
        </div>
        {navContent}
      </aside>
    </>
  );
}
